"use client";
// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.

import {
  createContext,
  forwardRef,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { Tooltip as TooltipPrimitive } from "radix-ui";
import { Slot } from "@radix-ui/react-slot";
import { AnimatePresence, motion } from "motion/react";
import { Kbd } from "@/components/wingo/ui/kbd";
import { useControllableState } from "@/components/wingo/hooks/use-controllable-state";
import { useLongPress, type LongPressHandlers } from "@/components/wingo/hooks/use-long-press";
import { useShortcutLabel } from "@/components/wingo/hooks/use-shortcut";
import { easings, presets, useMotionPreset, type MotionPreset } from "@/components/wingo/lib/motion";
import { colorVars, toneClasses, type Tone } from "@/components/wingo/lib/tones";
import { cn } from "@/components/wingo/lib/utils";
import { withRef } from "@/components/wingo/lib/with-ref";
import { useDefaults } from "@/components/wingo/lib/ui-config";

export type TooltipSide = "top" | "right" | "bottom" | "left";
export type TooltipAlign = "start" | "center" | "end";
export type TooltipSize = "sm" | "md";
export type TooltipRadius = "none" | "sm" | "md" | "lg" | "full";
export type TooltipSlot = "content" | "arrow" | "shortcut";
export type { Tone as TooltipTone };

const SIDES: TooltipSide[] = ["top", "right", "bottom", "left"];
const ALIGNS: TooltipAlign[] = ["start", "center", "end"];

// sm: one short label, centered and balanced when it wraps. md: a sentence, left aligned and filled line by line
// (balanced lines would leave the box at its max width with a ragged gap on the right).
// the width never passes the room radix measured on the chosen side, so a flipped tooltip wraps instead of leaving a phone screen
const SIZES: Record<TooltipSize, string> = {
  sm: "max-w-[min(16rem,var(--radix-tooltip-content-available-width,100vw))] px-2 py-1 text-center text-balance text-xs font-medium",
  md: "max-w-[min(18rem,var(--radix-tooltip-content-available-width,100vw))] px-3 py-2 text-start text-pretty text-[13px] leading-snug",
};

// md is the DESIGN look (8px on the small label); every step grows with the size so both read as the same shape.
// full is a pill on one line and a soft capsule on a wrapped sentence
const RADII: Record<TooltipRadius, Record<TooltipSize, string>> = {
  none: { sm: "rounded-none", md: "rounded-none" },
  sm: { sm: "rounded-[6px]", md: "rounded-[8px]" },
  md: { sm: "rounded-[8px]", md: "rounded-[10px]" },
  lg: { sm: "rounded-[10px]", md: "rounded-[14px]" },
  full: { sm: "rounded-full", md: "rounded-[18px]" },
};

// the arrow scales with the size; padding keeps it clear of the rounded corners
const ARROWS: Record<TooltipSize, { width: number; height: number }> = {
  sm: { width: 10, height: 5 },
  md: { width: 12, height: 6 },
};
const ARROW_PADDING: Record<TooltipRadius, number> = { none: 4, sm: 6, md: 8, lg: 10, full: 12 };

// inverted in both themes: neutral paints --primary (black on light, near-white on dark), other tones their fill
const CONTENT =
  "z-tooltip select-none break-words bg-[color:var(--tone)] text-[color:var(--tone-foreground)] shadow-floating outline-hidden";

// a long press on touch must not select text or open the ios callout
const TOUCH_TRIGGER = "[-webkit-touch-callout:none] pointer-coarse:select-none";

// a plain string child gets a focusable span, so keyboard users can reach the tooltip too
const FALLBACK_TRIGGER =
  "rounded-[4px] outline-hidden focus-visible:outline-2 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-ring";

// it starts 2px toward the trigger and settles away from it, growing out of the transform origin
const NUDGE: Record<TooltipSide, { x: number; y: number }> = {
  top: { x: 0, y: 2 },
  right: { x: -2, y: 0 },
  bottom: { x: 0, y: -2 },
  left: { x: 2, y: 0 },
};

const MOTION = Object.fromEntries(
  SIDES.map((side) => [
    side,
    {
      ...presets.tooltip,
      initial: { ...presets.tooltip.initial, ...NUDGE[side] },
      animate: { ...presets.tooltip.animate, x: 0, y: 0 },
    },
  ]),
) as Record<TooltipSide, MotionPreset>;

// in a group after the first one the label swaps in place: a quick fade, no grow, like the macos toolbar
const INSTANT: MotionPreset = {
  initial: { opacity: 0 },
  animate: { opacity: 1, scale: 1, x: 0, y: 0 },
  exit: presets.tooltip.exit,
  transition: { duration: 0.08, ease: easings.out },
};

const DEFAULT_DELAY = 300;
const DEFAULT_SKIP = 300;
// android focuses a tapped button right after the tap, and radix opens on focus; on touch only a long press opens it
const TOUCH_FOCUS_GUARD = 1000;

const hasContent = (node: ReactNode) =>
  node !== undefined && node !== null && node !== false && node !== true && !(typeof node === "string" && !node.trim());

// text cut off by truncate or line-clamp, on the trigger or anything inside it
function isClipped(el: HTMLElement | null) {
  if (!el) return true;
  const nodes = [el, ...Array.from(el.querySelectorAll<HTMLElement>("*"))];
  return nodes.some((node) => node.scrollWidth > node.clientWidth + 1 || node.scrollHeight > node.clientHeight + 1);
}

// ---------- group ----------

// the group remembers when its last tooltip closed, so the next one can tell it opened without a delay
type GroupContextValue = { markClosed: () => void; closedRecently: () => boolean };

const GroupContext = createContext<GroupContextValue | null>(null);

export type TooltipGroupProps = {
  // ms of hover before the first tooltip opens; the group owns it, so each tooltip's own delay is ignored
  delayDuration?: number;
  // moving to the next tooltip within this many ms opens it at once
  skipDelayDuration?: number;
  // close as soon as the pointer leaves the trigger, instead of letting it travel onto the tooltip
  disableHoverableContent?: boolean;
  children?: ReactNode;
};

// wrap a toolbar: the first tooltip waits for the delay, the ones after it open instantly while you move along
function TooltipGroupRender({
  delayDuration = DEFAULT_DELAY,
  skipDelayDuration = DEFAULT_SKIP,
  disableHoverableContent,
  children,
}: TooltipGroupProps) {
  const closedAt = useRef(-Infinity);
  const value = useMemo(
    () => ({
      markClosed: () => {
        closedAt.current = performance.now();
      },
      closedRecently: () => performance.now() - closedAt.current < skipDelayDuration,
    }),
    [skipDelayDuration],
  );
  return (
    <GroupContext.Provider value={value}>
      <TooltipPrimitive.Provider
        delayDuration={delayDuration}
        skipDelayDuration={skipDelayDuration}
        disableHoverableContent={disableHoverableContent}
      >
        {children}
      </TooltipPrimitive.Provider>
    </GroupContext.Provider>
  );
}

// ---------- root ----------

type RootContextValue = {
  // open and allowed to show
  open: boolean;
  // opened without a delay inside a group: enter with a quick fade
  instant: boolean;
  close: () => void;
  shortcut?: string;
  longPress: boolean;
  press: LongPressHandlers<HTMLElement>;
  // records touch and pen presses for the focus guard
  onTouchStart: (event: ReactPointerEvent<HTMLElement>) => void;
  triggerRef: RefObject<HTMLElement | null>;
};

const RootContext = createContext<RootContextValue | null>(null);

function useTooltip(part: string) {
  const context = useContext(RootContext);
  if (!context) throw new Error(`<${part}> must be used inside <TooltipRoot> (or <Tooltip>)`);
  return context;
}

export type TooltipRootProps = {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  // ms of hover before it opens; keyboard focus opens it at once. ignored inside a TooltipGroup
  delayDuration?: number;
  // never opens; the trigger works as usual
  disabled?: boolean;
  // on touch screens a long press on the trigger shows it (the click that would follow is swallowed)
  longPress?: boolean;
  // ms it stays after the finger lifts from a long press
  touchDuration?: number;
  // opens only while the trigger's text is cut off (truncate, line-clamp), for values that sometimes fit
  overflowOnly?: boolean;
  // a combo in mod+k notation, shown after the text and set as aria-keyshortcuts on the trigger. display only
  shortcut?: string;
  disableHoverableContent?: boolean;
  children?: ReactNode;
};

function TooltipRootRender({
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  delayDuration = DEFAULT_DELAY,
  disabled = false,
  longPress = true,
  touchDuration = 1500,
  overflowOnly = false,
  shortcut,
  disableHoverableContent,
  children,
}: TooltipRootProps) {
  const group = useContext(GroupContext);
  const [open, setOpen] = useControllableState({ value: openProp, defaultValue: defaultOpen, onChange: onOpenChange });
  const [instant, setInstant] = useState(false);
  const ownClosedAt = useRef(-Infinity);
  const touchedAt = useRef(-Infinity);
  const triggerRef = useRef<HTMLElement | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const markClosed = useCallback(() => {
    if (group) group.markClosed();
    else ownClosedAt.current = performance.now();
  }, [group]);
  const closedRecently = useCallback(
    () => (group ? group.closedRecently() : performance.now() - ownClosedAt.current < DEFAULT_SKIP),
    [group],
  );

  useEffect(() => () => clearTimeout(hideTimer.current), []);

  // a tooltip disabled while open must not come back on its own when it is enabled again
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled, setOpen]);

  const close = useCallback(() => {
    clearTimeout(hideTimer.current);
    markClosed();
    setOpen(false);
  }, [markClosed, setOpen]);

  // radix asks on hover, focus, escape, scroll and outside presses
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        close();
        return;
      }
      if (disabled) return;
      if (performance.now() - touchedAt.current < TOUCH_FOCUS_GUARD) return;
      if (overflowOnly && !isClipped(triggerRef.current)) return;
      clearTimeout(hideTimer.current);
      setInstant(closedRecently());
      setOpen(true);
    },
    [close, disabled, overflowOnly, closedRecently, setOpen],
  );

  const press = useLongPress<HTMLElement>(
    () => {
      if (overflowOnly && !isClipped(triggerRef.current)) return;
      clearTimeout(hideTimer.current);
      setInstant(false);
      setOpen(true);
    },
    {
      disabled: disabled || !longPress,
      // it stays while the finger is down, then reads for touchDuration before it goes
      onFinish: () => {
        clearTimeout(hideTimer.current);
        hideTimer.current = setTimeout(close, touchDuration);
      },
    },
  );

  const onTouchStart = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (event.pointerType !== "mouse") touchedAt.current = performance.now();
  }, []);

  const shown = open && !disabled;
  const context = useMemo(
    () => ({ open: shown, instant, close, shortcut, longPress: longPress && !disabled, press, onTouchStart, triggerRef }),
    [shown, instant, close, shortcut, longPress, disabled, press, onTouchStart],
  );

  const root = (
    <TooltipPrimitive.Root
      open={shown}
      onOpenChange={handleOpenChange}
      // inside a group the group's delay wins
      delayDuration={group ? undefined : delayDuration}
      disableHoverableContent={disableHoverableContent}
    >
      <RootContext.Provider value={context}>{children}</RootContext.Provider>
    </TooltipPrimitive.Root>
  );

  // every tooltip works without setup: outside a group it brings its own provider
  if (group) return root;
  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration} skipDelayDuration={DEFAULT_SKIP}>
      {root}
    </TooltipPrimitive.Provider>
  );
}

// ---------- trigger ----------

type TriggerSlotProps = ComponentProps<"button"> & { asChild?: boolean; "data-state"?: string };

// radix spreads data-state and an undefined aria-describedby onto the trigger. on a component child (a toggle, a radio
// item, a field) those would override the child's own, so only defined props pass and the state is renamed
// React 18: Radix îi dă ref-ul declanșatorului prin Slot (ancora tooltip-ului) — fără forwardRef s-ar pierde.
const TriggerSlot = forwardRef<HTMLButtonElement, TriggerSlotProps>(function TriggerSlot({ asChild = false, "data-state": state, ...props }, ref) {
  const defined = Object.fromEntries(Object.entries(props).filter(([, value]) => value !== undefined));
  if (asChild) return <Slot ref={ref} data-tooltip-state={state} {...defined} />;
  return <button ref={ref} type="button" data-tooltip-state={state} {...defined} />;
});

export type TooltipTriggerProps = ComponentProps<"button"> & {
  // render the single child as the trigger (a Button, an <a>, a span with tabIndex 0); it must take a ref
  asChild?: boolean;
};

function TooltipTriggerRender({ asChild = false, className, children, ...props }: TooltipTriggerProps) {
  const { press, longPress, onTouchStart, shortcut, triggerRef } = useTooltip("TooltipTrigger");
  const { aria } = useShortcutLabel(shortcut);
  // a component child (Button, ToggleGroupItem) keeps its own data-slot; a plain element is marked as the trigger
  const host = !asChild || (isValidElement(children) && typeof children.type === "string");

  return (
    <TooltipPrimitive.Trigger
      asChild
      ref={triggerRef as RefObject<HTMLButtonElement>}
      {...press}
      onPointerDown={(event) => {
        onTouchStart(event);
        press.onPointerDown(event);
      }}
      onPointerLeave={(event) => {
        press.onPointerLeave(event);
        // a finger "leaves" as it lifts; with disableHoverableContent radix would close a long-pressed tooltip right there
        if (event.pointerType === "touch") event.preventDefault();
      }}
    >
      <TriggerSlot
        asChild={asChild}
        data-slot={host ? "tooltip-trigger" : undefined}
        aria-keyshortcuts={aria}
        className={cn(longPress && TOUCH_TRIGGER, className) || undefined}
        {...props}
      >
        {children}
      </TriggerSlot>
    </TooltipPrimitive.Trigger>
  );
}

// ---------- content ----------

export type TooltipContentProps = Omit<
  ComponentProps<typeof TooltipPrimitive.Content>,
  "asChild" | "forceMount" | "color" | "side" | "align" | "content"
> & {
  // flips to the other side when there is no room
  side?: TooltipSide;
  align?: TooltipAlign;
  // px between the trigger and the tooltip (the arrow adds its own height)
  sideOffset?: number;
  // a small arrow in the tooltip's color, pointing at the trigger
  arrow?: boolean;
  // neutral is the inverted look (black on light, near-white on dark); other tones fill with their color
  tone?: Tone;
  // any css color; replaces the tone, text turns black or white by lightness
  color?: string;
  // sm: one short label, md: a sentence up to 288px wide
  size?: TooltipSize;
  radius?: TooltipRadius;
  // where the portal mounts; document.body by default
  container?: HTMLElement | null;
  classNames?: Partial<Record<TooltipSlot, string>>;
};

function TooltipContentRender(tooltipContentProps: TooltipContentProps) {
  const {
    side = "top",
    align = "center",
    sideOffset = 6,
    arrow = false,
    tone = "neutral",
    color,
    size = "sm",
    radius = "md",
    container,
    collisionPadding = 12,
    arrowPadding,
    classNames,
    className,
    style,
    children,
    onPointerDown,
    ...props
  } = useDefaults("tooltip-content", tooltipContentProps);
  const { open, instant, close, shortcut } = useTooltip("TooltipContent");
  const safeSide: TooltipSide = SIDES.includes(side) ? side : "top";
  const safeAlign: TooltipAlign = ALIGNS.includes(align) ? align : "center";
  const safeSize: TooltipSize = Object.hasOwn(SIZES, size) ? size : "sm";
  const safeRadius: TooltipRadius = Object.hasOwn(RADII, radius) ? radius : "md";
  const safeTone: Tone = Object.hasOwn(toneClasses, tone) ? tone : "neutral";
  const motionProps = useMotionPreset(instant ? INSTANT : MOTION[safeSide]);
  const arrowSize = ARROWS[safeSize];

  return (
    <AnimatePresence>
      {open && (
        <TooltipPrimitive.Portal forceMount container={container}>
          <TooltipPrimitive.Content
            forceMount
            asChild
            side={safeSide}
            align={safeAlign}
            sideOffset={sideOffset}
            collisionPadding={collisionPadding}
            arrowPadding={arrowPadding ?? ARROW_PADDING[safeRadius]}
            // a touch on the tooltip itself dismisses it, like a touch anywhere else
            onPointerDown={(event) => {
              onPointerDown?.(event);
              if (!event.defaultPrevented && event.pointerType !== "mouse") close();
            }}
            {...props}
          >
            <motion.div
              {...motionProps}
              data-slot="tooltip-content"
              data-tone={color ? "custom" : safeTone}
              data-size={safeSize}
              className={cn(
                toneClasses[safeTone],
                CONTENT,
                SIZES[safeSize],
                RADII[safeRadius][safeSize],
                classNames?.content,
                className,
              )}
              style={{
                transformOrigin: "var(--radix-tooltip-content-transform-origin)",
                ...colorVars(color),
                ...(style as object),
              }}
            >
              {children}
              {shortcut?.trim() && (
                <Kbd
                  data-slot="tooltip-shortcut"
                  shortcut={shortcut}
                  combined
                  variant="ghost"
                  hideOnTouch
                  // the tooltip's own text at 60%, at the size of the text around it
                  className={cn("ms-2 h-auto min-w-0 px-0 align-baseline text-[1em] text-inherit opacity-60", classNames?.shortcut)}
                />
              )}
              {arrow && (
                <TooltipPrimitive.Arrow asChild width={arrowSize.width} height={arrowSize.height}>
                  {/* a rounded tip; the 1px above the base tucks under the body so no seam shows */}
                  <svg
                    data-slot="tooltip-arrow"
                    viewBox="0 0 10 5"
                    aria-hidden
                    className={cn("block overflow-visible fill-[color:var(--tone)]", classNames?.arrow)}
                  >
                    <path d="M0 -1H10V0L6.06 3.94A1.5 1.5 0 0 1 3.94 3.94L0 0Z" />
                  </svg>
                </TooltipPrimitive.Arrow>
              )}
            </motion.div>
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      )}
    </AnimatePresence>
  );
}

// ---------- the simple api ----------

export type TooltipProps = Omit<TooltipContentProps, "children"> &
  Omit<TooltipRootProps, "children" | "disableHoverableContent"> & {
    // the text; empty renders the trigger alone
    content?: ReactNode;
    // the trigger, rendered asChild: a Button, an <a>, or a span with tabIndex 0. it must take a ref
    children?: ReactNode;
  };

function TooltipRender(tooltipProps: TooltipProps) {
  const {
    content,
    children,
    shortcut,
    side = "top",
    align = "center",
    sideOffset = 6,
    delayDuration = DEFAULT_DELAY,
    arrow = false,
    tone = "neutral",
    color,
    size = "sm",
    radius = "md",
    open,
    defaultOpen = false,
    onOpenChange,
    disabled = false,
    longPress = true,
    touchDuration = 1500,
    overflowOnly = false,
    container,
    classNames,
    ...props
  } = useDefaults("tooltip", tooltipProps);
  const trigger = isValidElement(children) ? (
    children
  ) : (
    <span tabIndex={0} className={FALLBACK_TRIGGER}>
      {children}
    </span>
  );

  return (
    <TooltipRoot
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      delayDuration={delayDuration}
      disabled={disabled || !hasContent(content)}
      longPress={longPress}
      touchDuration={touchDuration}
      overflowOnly={overflowOnly}
      shortcut={shortcut}
    >
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        arrow={arrow}
        tone={tone}
        color={color}
        size={size}
        radius={radius}
        container={container}
        classNames={classNames}
        {...props}
      >
        {content}
      </TooltipContent>
    </TooltipRoot>
  );
}

// React 18: ref-urile ajung la randare doar prin forwardRef (vezi lib/with-ref.ts)
export const TooltipGroup = withRef(TooltipGroupRender, "TooltipGroup");
export const TooltipRoot = withRef(TooltipRootRender, "TooltipRoot");
export const TooltipTrigger = withRef(TooltipTriggerRender, "TooltipTrigger");
export const TooltipContent = withRef(TooltipContentRender, "TooltipContent");
export const Tooltip = withRef(TooltipRender, "Tooltip");
